import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiCheck, FiUpload, FiFile, FiX } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import { organizationsApi, usersApi } from '../../../services/api';

const DOCUMENT_TYPES = [
  'SOC 2 Type 2',
  'SOC 2 Type 1',
  'ISO 27001',
  'ISO 27701',
  'Penetration Test Report',
  'GDPR Data Processing Agreement',
  'Business Continuity Plan',
  'Vulnerability Assessment Report',
  'Privacy Policy',
  'Other',
];

const emptyForm = {
  documentName: '',
  documentType: '',
  customDocumentType: '',
  startDate: '',
  endDate: '',
};

const ShareDocumentModal = ({ isOpen, onClose, onSave, initialData, isEditing }) => {
  const [form, setForm] = useState(emptyForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Org / user lists
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  // ── Load organizations once modal opens ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setOrgsLoading(true);
    organizationsApi
      .getAll(1, 10000)
      .then((res) => setOrganizations(res.data || res || []))
      .catch(() => setOrganizations([]))
      .finally(() => setOrgsLoading(false));
  }, [isOpen]);

  // ── Pre-fill when editing ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    if (isEditing && initialData) {
      const knownType = DOCUMENT_TYPES.includes(initialData.documentType);
      setForm({
        documentName: initialData.documentName || '',
        documentType: initialData.documentType
          ? (knownType ? initialData.documentType : 'Other')
          : '',
        customDocumentType: initialData.documentType && !knownType ? initialData.documentType : '',
        startDate: initialData.startDate ? initialData.startDate.split('T')[0] : '',
        endDate: initialData.endDate ? initialData.endDate.split('T')[0] : '',
      });
      setSelectedOrgId(initialData.organizationId || null);
      setSelectedUserId(initialData.sharedWithUserId || null);
      setSelectedFile(null);
    } else {
      setForm(emptyForm);
      setSelectedOrgId(null);
      setSelectedUserId(null);
      setSelectedFile(null);
    }
    setErrors({});
    setUserSearch('');
  }, [isOpen, isEditing, initialData]);

  // ── Load users when org changes ────────────────────────────────────────────
  useEffect(() => {
    if (!selectedOrgId) {
      setUsers([]);
      setFilteredUsers([]);
      setSelectedUserId(null);
      return;
    }
    setUsersLoading(true);
    usersApi
      .getAll(1, 10000, null, null, null, { organizationId: selectedOrgId })
      .then((res) => {
        const list = res.data || res || [];
        setUsers(list);
        setFilteredUsers(list);
      })
      .catch(() => {
        setUsers([]);
        setFilteredUsers([]);
      })
      .finally(() => setUsersLoading(false));
  }, [selectedOrgId]);

  // ── Filter users by search ─────────────────────────────────────────────────
  useEffect(() => {
    if (!userSearch.trim()) {
      setFilteredUsers(users);
      return;
    }
    const s = userSearch.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(s) ||
          (u.username || '').toLowerCase().includes(s) ||
          (u.email || '').toLowerCase().includes(s)
      )
    );
  }, [userSearch, users]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.documentName.trim()) e.documentName = 'Document name is required.';
    if (!form.documentType) e.documentType = 'Document type is required.';
    if (form.documentType === 'Other' && !form.customDocumentType.trim())
      e.customDocumentType = 'Please enter a document type.';
    if (!selectedOrgId) e.organization = 'Please select an organization.';
    if (!selectedUserId) e.user = 'Please select a user.';
    if (!isEditing && !selectedFile) e.file = 'Please upload a document file.';
    if (form.startDate && form.endDate && form.startDate > form.endDate)
      e.endDate = 'End date must be after start date.';
    return e;
  };

  // ── Handle submit ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('documentName', form.documentName);
      fd.append('documentType', form.documentType === 'Other' ? form.customDocumentType.trim() : form.documentType);
      fd.append('organizationId', selectedOrgId);
      fd.append('organizationName', selectedOrg?.name || selectedOrg?.organizationName || '');
      fd.append('sharedWithUserId', selectedUserId);
      fd.append('sharedWithUsername', `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || selectedUser?.username || '');
      fd.append('sharedWithUserEmail', selectedUser?.email || '');
      if (form.startDate) fd.append('startDate', form.startDate);
      if (form.endDate) fd.append('endDate', form.endDate);
      if (selectedFile) fd.append('file', selectedFile);
      await onSave(fd);
    } finally {
      setSaving(false);
    }
  };

  // ── File input handler ─────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    if (!form.documentName.trim()) {
      // Auto-fill name from file (strip extension)
      setForm((prev) => ({
        ...prev,
        documentName: file.name.replace(/\.[^/.]+$/, ''),
      }));
    }
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Document' : 'Add Document'}
      size="xl"
      footer={
        <div className="w-full flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-8 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Share Document'}
          </button>
        </div>
      }
    >
      <div className="space-y-6 py-2">

        {/* ── Row 1: Document Name + Type ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Document Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.documentName}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, documentName: e.target.value }));
                setErrors((prev) => ({ ...prev, documentName: undefined }));
              }}
              placeholder="e.g. SOC 2 Type 2 Report 2025"
              className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0077b5] ${
                errors.documentName ? 'border-red-400' : 'border-slate-200'
              }`}
            />
            {errors.documentName && (
              <p className="mt-1 text-xs text-red-500">{errors.documentName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.documentType}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, documentType: e.target.value }));
                setErrors((prev) => ({ ...prev, documentType: undefined }));
              }}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#0077b5] ${
                errors.documentType ? 'border-red-400' : 'border-slate-200'
              }`}
            >
              <option value="">Select document type...</option>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {errors.documentType && (
              <p className="mt-1 text-xs text-red-500">{errors.documentType}</p>
            )}
            {form.documentType === 'Other' && (
              <div className="mt-3">
                <input
                  type="text"
                  value={form.customDocumentType}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, customDocumentType: e.target.value }));
                    setErrors((prev) => ({ ...prev, customDocumentType: undefined }));
                  }}
                  placeholder="Enter document type"
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0077b5] ${
                    errors.customDocumentType ? 'border-red-400' : 'border-slate-200'
                  }`}
                />
                {errors.customDocumentType && (
                  <p className="mt-1 text-xs text-red-500">{errors.customDocumentType}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 2: Date Range ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Start Date</label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0077b5]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">End Date</label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, endDate: e.target.value }));
                setErrors((prev) => ({ ...prev, endDate: undefined }));
              }}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0077b5] ${
                errors.endDate ? 'border-red-400' : 'border-slate-200'
              }`}
            />
            {errors.endDate && (
              <p className="mt-1 text-xs text-red-500">{errors.endDate}</p>
            )}
          </div>
        </div>

        {/* ── Row 3: Org + User ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Organization */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-2">
              Organization <span className="text-red-500">*</span>
              {selectedOrg && (
                <span className="ml-2 text-[#0b79b8] font-semibold">• {selectedOrg.name}</span>
              )}
            </h3>
            <div className={`border rounded-lg ${errors.organization ? 'border-red-400' : 'border-slate-200'}`}>
              {orgsLoading ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">Loading...</div>
              ) : (
                <div className="max-h-52 overflow-y-auto">
                  {organizations.filter((o) => o.trustCenterEnabled !== false).map((org) => (
                    <div
                      key={org.id}
                      onClick={() => {
                        setSelectedOrgId(org.id === selectedOrgId ? null : org.id);
                        setSelectedUserId(null);
                        setUserSearch('');
                        setErrors((prev) => ({ ...prev, organization: undefined, user: undefined }));
                      }}
                      className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                        selectedOrgId === org.id
                          ? 'bg-[#0077b5]/10 text-slate-700'
                          : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedOrgId === org.id
                            ? 'border-[#0077b5] bg-[#0077b5]'
                            : 'border-slate-300'
                        }`}
                      >
                        {selectedOrgId === org.id && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="text-sm">{org.name}</span>
                    </div>
                  ))}
                  {organizations.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">
                      No organizations available
                    </div>
                  )}
                </div>
              )}
            </div>
            {errors.organization && (
              <p className="mt-1 text-xs text-red-500">{errors.organization}</p>
            )}
          </div>

          {/* User */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-2">
              User <span className="text-red-500">*</span>
              {selectedUser && (
                <span className="ml-2 text-[#0b79b8] font-semibold">
                  • {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.username}
                </span>
              )}
            </h3>
            {!selectedOrgId ? (
              <div className="border border-slate-200 rounded-lg px-4 py-10 text-center">
                <p className="text-sm text-slate-400">Select an organization first</p>
              </div>
            ) : (
              <div className={`border rounded-lg ${errors.user ? 'border-red-400' : 'border-slate-200'}`}>
                {/* Search */}
                <div className="relative border-b border-slate-200">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0077b5] rounded-t-lg"
                  />
                </div>
                <div className="max-h-44 overflow-y-auto">
                  {usersLoading ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">Loading...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">
                      {userSearch.trim() ? 'No users match your search' : 'No users in this organization'}
                    </div>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => {
                          setSelectedUserId(u.id === selectedUserId ? null : u.id);
                          setErrors((prev) => ({ ...prev, user: undefined }));
                        }}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                          selectedUserId === u.id
                            ? 'bg-[#0077b5]/10 text-slate-700'
                            : 'text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedUserId === u.id
                              ? 'border-[#0077b5] bg-[#0077b5]'
                              : 'border-slate-300'
                          }`}
                        >
                          {selectedUserId === u.id && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || `User ${u.id}`}
                          </span>
                          {u.email && <span className="text-xs text-slate-400">{u.email}</span>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            {errors.user && (
              <p className="mt-1 text-xs text-red-500">{errors.user}</p>
            )}
          </div>
        </div>

        {/* ── Row 4: File Upload ── */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">
            Upload Document {!isEditing && <span className="text-red-500">*</span>}
            {isEditing && <span className="text-slate-400 font-normal ml-1">(leave empty to keep existing file)</span>}
          </label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              errors.file ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-[#0077b5] hover:bg-blue-50/30'
            }`}
          >
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FiFile className="text-[#0077b5] text-2xl" />
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-700">{selectedFile.name}</p>
                  <p className="text-xs text-slate-400">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="ml-2 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                >
                  <FiX />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <FiUpload className="text-slate-300 text-3xl" />
                <p className="text-sm text-slate-500">
                  <span className="font-medium text-[#0077b5]">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-slate-400">PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG</p>
              </div>
            )}
          </div>
          {errors.file && (
            <p className="mt-1 text-xs text-red-500">{errors.file}</p>
          )}
        </div>

      </div>
    </Modal>
  );
};

export default ShareDocumentModal;
